import { CSSProperties } from 'react';
import styled from 'styled-components';
import type { RepresentationData } from '@ardoq/data-model';
import { IconName } from '@ardoq/icons';
import { colors, s4, s8 } from '@ardoq/design-tokens';
import { GroupWithGridInfo } from '../types';
import { ComponentHeaderCardContent } from './componentHeaderCardContent';
import { getVisibleChildCount } from '../../util';

const COLUMN_HEADER_ICON_SIZE = 16;

/**
 * The stripe covers only the top portion of the grid cell.
 * When the column is expanded, the parent HeaderCell spans into child rows —
 * the stripe must be a fixed height so the white body area shows through below.
 */
const CardStripe = styled.div<{ $isDark: boolean }>`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${s4};
  padding: ${s8};
  overflow: hidden;
  color: ${({ $isDark }) =>
    $isDark ? colors.textOnInvertedStrong : colors.textDefault};
`;

type ColumnHeaderComponentArgs = {
  representationData: RepresentationData;
  group: GroupWithGridInfo;
  showExpander: boolean;
  isExpanded: boolean;
  isHighlighted: boolean;
  isContext: boolean;
  stripeStyle: CSSProperties;
  stripeHeight: number;
  isDark: boolean;
  /** When true (cell at the deepest renderable level and not an expanded
   * parent), the stripe fills the cell's full height with `stripeHeight`
   * as a floor — used so column headers stretch vertically when the
   * column-headers area is shorter than the level-headers area on the
   * left (ARD-31376). When false (expanded parent rendered above a
   * GroupOutline), keep the explicit `stripeHeight` so the body shows
   * through below the stripe. */
  fillCell: boolean;
  expanderClicked: VoidFunction;
  labelDoubleClicked: VoidFunction;
};

const ColumnHeaderComponent = ({
  representationData,
  group,
  showExpander,
  expanderClicked,
  labelDoubleClicked,
  isExpanded,
  isHighlighted,
  isContext,
  stripeStyle,
  stripeHeight,
  isDark,
  fillCell,
}: ColumnHeaderComponentArgs) => (
  <CardStripe
    $isDark={isDark}
    style={{
      ...stripeStyle,
      ...(fillCell
        ? { height: '100%', minHeight: stripeHeight }
        : { height: stripeHeight }),
    }}
  >
    <ComponentHeaderCardContent
      componentLabel={group.label}
      representationData={representationData}
      otherLabels={group.otherLabels}
      otherLabelWrappedLines={group.otherLabelWrappedLines}
      childCount={showExpander && !isExpanded ? getVisibleChildCount(group) : 0}
      showExpander={showExpander}
      isHighlighted={isHighlighted}
      isContext={isContext}
      isDark={isDark}
      iconSize={COLUMN_HEADER_ICON_SIZE}
      expanderIconName={
        isExpanded ? IconName.UNFOLD_LESS : IconName.UNFOLD_MORE
      }
      wrapLabel
      expanderClicked={expanderClicked}
      labelDoubleClicked={labelDoubleClicked}
    />
  </CardStripe>
);

export { ColumnHeaderComponent };
